/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from 'react';
import dayjs from 'dayjs';

import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import LinearProgress from '@mui/material/LinearProgress';
import MicIcon from '@mui/icons-material/Mic';
import CircularProgressCountDown from 'components/CircularProgressCountDown';
import CustomButton from 'components/CustomButton';
import TroubleshootDialog from './TroubleshootDialog';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';

import { useSnackbar } from "contexts/SnackbarProvider";
import { useNavigateWithClient } from 'hooks';
import { Session, askPermission, formatDate, getClientPrefix, subscribeToAudioLevel } from 'utils';
import { PersonalizedTemplateType } from 'components/CreateInterview/constants';

import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
    resumeActions: {
        display: 'flex', width: "100%",
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2)
    },
    reset: {
        width: "50%", fontFamily: 'Montserrat', fontWeight: 500, fontSize: 15,
        color: theme.palette.primary.main,
        cursor: 'pointer',
        border: "1px solid",
        borderColor: theme.palette.primary.main,
        textTransform: "none"
    },
    saveBtn: {
        width: "50%", marginRight: theme.spacing(5),
        textTransform: "none"
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(4, 6)
    },
    closeIcon: {
        color: 'black', padding: theme.spacing(1),
        '&:hover': {
            boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
        }
    },
    contentContainer: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', padding: theme.spacing(4, 6),
        marginTop: '15px'
    },
    label: {
        ...theme.typography['body01-semiBold']
    },
    value: {
        ...theme.typography['body01-bold'],
        color: theme.palette.primary['clr-300'],
        width: '450px',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        overflow: 'hidden',
    },
    instructions: {
        ...theme.typography['body01-semiBold'],
        marginBottom: theme.spacing(2)
    },
    micRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1)
    },
    personalizedContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: theme.spacing(5)
    },
    contentBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1)
    },
    microphoneTest: {
        display: 'flex',
        width: "335px",
        gap: theme.spacing(4),
        height: '28px',
        justifyContent: 'center',
        alignItems: 'center',
        border: `1px solid ${theme.palette.neutral['clr-200']}`,
        borderRadius: theme.spacing(3)
    },
    micBox: {
        display: 'flex',
        gap: theme.spacing(3),
        alignItems: 'center'
    },
    levelBar: {
        width: '70px'
    },
    envoirnmntBox: {
        padding: theme.spacing(2, 4),
        backgroundColor: theme.palette.success['clr-100'],
        width: '335px',
        borderRadius: '4px'
    },
    envoirnmntBoxDaf: {
        padding: theme.spacing(2, 4),
        backgroundColor: theme.palette.success['clr-100'],
        width: '335px',
        borderRadius: '4px'
    }
}));

const PaperProps = { style: { borderRadius: '8px', position: 'relative', minHeight: 430 } }

const neverExpiryYear = 9999;

function StartAssessmentDialog({
    open = true,
    template = {
        _id: "", name: "", expiresOn: new Date(),
        metadata: { testTime: 55 }, type: "practice"
    },
    isPersonalizedInterview = false,
    setCountdownStart,
    clientMock = false,
    userMock = false,
    dafInterview = false,
    codingInterview = false,
    stateParams = {},
    onClose = () => { },
}) {
    const classes = useStyles();
    const navigateWithClient = useNavigateWithClient();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const theme = useTheme();

    const [state, setState] = React.useState("INITIAL");
    const [audioLevel, setAudioLevel] = React.useState(0);
    const [matched, setMatched] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [openTroubleshootDialog, setOpenTroubleshootDialog] = React.useState(false);

    const unsubscribeRef = React.useRef();
    const { testTime } = template?.metadata || {};
    const user = Session.userId;

    const instructions = {
        "assignment": [
            <>You <b>can only attempt this assessment once</b></>,
            <>You will have a certain amount of time to complete each question, so manage your time carefully.</>,
            <>If you don't want to attempt this assessment now, click on 'x' icon on the top right corner.</>
        ],
        "practice": [
            <>This is a <b>practice</b> assessment, you can attempt it <b>
                {
                    (template?.maxAllowedAttempts > 0) ?
                        `${template?.maxAllowedAttempts} times.` :
                        'as many times you want.'}
            </b></>,
            <>You will have a certain amount of time to complete each question, so manage your time carefully.</>,
            <>If you don't want to attempt this assessment now, click on 'x' icon on the top right corner.</>
        ],
        "follow_up": [
            <>This is a <b>follow up</b> assessment, you can attempt it <b>
                {
                    (template?.maxAllowedAttempts > 0) ?
                        `${template?.maxAllowedAttempts} times.` :
                        'as many times you want.'}
            </b></>,
            <>You will have a certain amount of time to complete each question, so manage your time carefully.</>,
            <>If you don't want to attempt this assessment now, click on 'x' icon on the top right corner..</>
        ],
        "pathway": [
            <>This is a <b>pathway</b> assessment, you can attempt it <b>
                {
                    (template?.maxAllowedAttempts > 0) ?
                        `${template?.maxAllowedAttempts} times.` :
                        'as many times you want.'}
            </b></>,
            <>You will have a certain amount of time to complete each question, so manage your time carefully.</>,
            <>If you don't want to attempt this assessment now, click on 'x' icon on the top right corner.</>
        ],
        "personalized": template?.metadata?.personalizedTemplateType === PersonalizedTemplateType.exam
            ? [
                <>This is a <b>personalized interview</b>. You can practice it multiple times.</>,
                <> Dress formally to ensure a professional interview experience.</>,
                <>Use purposeful hand gestures to enhance engagement and clarity.</>,
                <b> Wearing an external microphone would help you to record answers at a High Quality. </b>
            ]
            : clientMock
                ?
                [
                    <>This is a <b>mock interview</b>. You can practice it multiple times.</>,
                    <> Carefully listen to the interviewer's question.</>,
                    <b> Wearing an external microphone would help you to record answers at a High Quality. </b>
                ]
                :
                [
                    <>This is a <b>personalized interview</b>. You can practice it multiple times.</>,
                    <> Carefully listen to the interviewer's question.</>,
                    <b> Wearing an external microphone would help you to record answers at a High Quality. </b>
                ],
        "coding": [
            <>You need to solve <b style={{ fontWeight: 700, color: '#111519' }}>1 coding problem</b> with <b style={{ fontWeight: 700, color: '#111519' }}>2 follow-up coding & verbal explanation questions</b>.</>,
            <>You will get <b style={{ fontWeight: 700, color: '#111519' }}>separate timers</b> for solving the coding problem and the follow-up questions.</>,
            <>You can take up to <b style={{ fontWeight: 700, color: '#111519' }}>2 hints</b> for the coding problem and <b style={{ fontWeight: 700, color: '#111519' }}>1 for the follow-up questions</b>. <b style={{ fontWeight: 700, color: '#111519' }}>Beware:</b> taking hints will lower your score.</>,
            <>More than 10 AI-proctoring errors such as <b style={{ fontWeight: 700, color: '#111519' }}>switching tabs, </b>and leaving the screen, might result in a <b style={{ fontWeight: 700, color: '#111519' }}>penalty</b>.</>,
            <>Submit at least one question to view feedback.</>,
            <>You can end the challenge anytime; <b style={{ fontWeight: 700, color: '#111519' }}>unsubmitted questions</b> will lower your score.</>,
            <><b style={{ fontWeight: 700, color: '#111519' }}>Do not refresh</b> the page during the assessment. It will result in loss of progress or auto-submission.</>,
        ],
        "case_master": [
            <>You will analyze <b style={{ fontWeight: 700, color: '#111519' }}>1 business case study</b> with <b style={{ fontWeight: 700, color: '#111519' }}>verbal responses</b> to case questions.</>,
            <>You will get <b style={{ fontWeight: 700, color: '#111519' }}>separate timers</b> for each section of the case analysis.</>,
            <>Listen carefully to the interviewer's questions and provide structured responses.</>,
            <>More than 10 AI-proctoring errors such as <b style={{ fontWeight: 700, color: '#111519' }}>switching tabs, </b>and leaving the screen, might result in a <b style={{ fontWeight: 700, color: '#111519' }}>penalty</b>.</>,
            <>Speak clearly and ensure your microphone is working properly for best results.</>,
            <>You can end the interview anytime; <b style={{ fontWeight: 700, color: '#111519' }}>unanswered questions</b> will affect your score.</>,
            <><b style={{ fontWeight: 700, color: '#111519' }}>Do not refresh</b> the page during the assessment. It will result in loss of progress or auto-submission.</>,
        ],
        "case_master_contest": [
            <>You have <b style={{ fontWeight: 700, color: '#111519' }}>only one attempt</b>. Do not start unless you are fully prepared.</>,
            <>Once started, the case must be completed in one go.</>,
            <>AI proctoring is active. Avoid tab switching or leaving the screen.</>,
            <>Exiting will <b style={{ fontWeight: 700, color: '#111519' }}>end your attempt</b>, and you will not be able to try again.</>,
            <>Listen carefully and provide <b style={{ fontWeight: 700, color: '#111519' }}>clear, structured responses</b> throughout the case.</>,
            <>Ensure your <b style={{ fontWeight: 700, color: '#111519' }}>microphone and camera are working</b>, and grant all required permissions. Maintain a stable internet connection and <b style={{ fontWeight: 700, color: '#111519' }}>close all distractions</b> before starting.</>,
            <>Do not refresh the page during the assessment. It will result in loss of progress or auto-submission.</>,
        ]
    };

    const proceedToTest = async () => {
        try {
            setLoading(true);

            const { _id, maxAllowedAttempts, attempts, type } = template;

            if (
                type === 'practice' &&
                maxAllowedAttempts &&
                maxAllowedAttempts !== -1 &&
                attempts >= maxAllowedAttempts
            ) {
                throw new Error(
                    'You have reached max limit of attempts for this assessment!'
                );
            }
            if (codingInterview) {
                navigate(`/${getClientPrefix(user)}/coding-assessment?cid=${_id}`, { state: stateParams });
            } else if (template?.type === 'case_master' || stateParams?.data === 'case_master') {
                console.log('Navigating to case-master-assessment:', { template, stateParams });
                navigate(`/${getClientPrefix(user)}/case-master-assessment?cid=${_id}`, { state: stateParams });
            } else {
                console.log('Navigating to default assessment:', { template, stateParams });
                navigateWithClient(`/assessment?tid=${_id}`);
            }

        } catch (error) {
            console.error(error);
            snackbar.error(error.message);
        } finally {
            setLoading(false);
        }
    }

    const openPermissionPrompt = async () => {
        const isAudioPermitted = await askPermission({ audio: true });
        askPermission({ video: true }).catch(() => { });

        if (isAudioPermitted) {
            subscribe();
            setState("GRANTED");
        } else {
            setState("ASK_PERMISSION");
        }
    }

    useEffect(() => {
        if (isPersonalizedInterview) {
            if (matched) {
                setCountdownStart(true)
            }
        }
    }, [matched])

    useEffect(() => {
        if (open) {
            setMatched(false);

            (async () => {
                const isAudioPermitted = await askPermission({ audio: true });
                askPermission({ video: true }).catch(() => { });

                if (isAudioPermitted) {
                    subscribe();
                    setState("GRANTED");
                } else {
                    setState("ASK_PERMISSION");
                }
            })();
        }
    }, [open]);


    const subscribe = React.useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false
            });

            unsubscribeRef.current = subscribeToAudioLevel(stream, (level) => setAudioLevel(level));
            console.log("subscribed.");
        } catch (error) {
            console.error(error);
        }
    }, []);

    const reset = React.useCallback(async () => {
        if (unsubscribeRef.current && typeof unsubscribeRef.current === 'function') {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
            console.log('unsubscribed.');
        }
        setMatched(false);
    }, []);

    const handleClose = () => {
        reset();
        onClose();
    }

    return (
        <>
            {isPersonalizedInterview
                ? (
                    <Box display='flex' flexDirection='column' gap='20px' alignItems='center'>
                        {dafInterview && (
                            <Box className={classes.envoirnmntBoxDaf}>
                                <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
                                    {[
                                        "Sit in a quiet environment and use an external microphone.",
                                        "Dress formally to ensure a professional interview experience.",
                                        "Use purposeful hand gestures to enhance engagement and clarity."
                                    ].map((text, index) => (
                                        <li
                                            key={index}
                                            style={{ display: 'flex', alignItems: 'center', marginBottom: index !== 2 ? '4px' : '0' }}
                                        >
                                            <Typography variant='body02-bold' color='success.clr-700'>
                                                • {text}
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        )}
                        <Box className={classes.personalizedContainer}>
                            <Typography variant='h5-medium' color='neutral.clr-700'>
                                Are you ready for the interview?
                            </Typography>
                            <Box className={classes.contentBox}>
                                <Box display='flex' alignItems='center' gap={2}>
                                    <Typography variant='h5-semiBold' color='neutral.clr-400'>
                                        Ready to begin?
                                    </Typography>
                                    <CustomButton
                                        variant='contained'
                                        disabled={state !== 'GRANTED'}
                                        onClick={() => setMatched(true)}
                                    >
                                        Start Assessment
                                    </CustomButton>
                                </Box>
                            </Box>
                            <Box className={classes.microphoneTest}>
                                <Box className={classes.micBox}>
                                    <MicIcon style={{ fontSize: '18px' }} />
                                    <Box className={classes.levelBar}>
                                        <LinearProgress
                                            variant="determinate" value={audioLevel}
                                            style={{ borderRadius: '10px' }}
                                        />
                                    </Box>
                                </Box>
                                <Typography
                                    onClick={() => setOpenTroubleshootDialog(true)}
                                    variant='body01-link'
                                    color='primary.clr-300'
                                    sx={{ cursor: 'pointer' }}
                                >
                                    Troubleshoot microphone
                                </Typography>
                            </Box>
                            <TroubleshootDialog
                                open={openTroubleshootDialog}
                                onClose={() => setOpenTroubleshootDialog(false)}
                            />
                            {!dafInterview && (
                                <Box className={classes.envoirnmntBox}>
                                    <Typography variant='body02-bold' color='success.clr-700'>
                                        For best results, sit in a quiet environment and use an external microphone.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )
                : (
                    <>
                        <Dialog
                            open={open} PaperProps={PaperProps} maxWidth={'md'}
                            onClose={(e, reason) => (reason === 'backdropClick') ? null : handleClose()}
                            BackdropProps={{
                                className: classes.backdrop
                            }}
                        >
                            {
                                !matched ?
                                    <>
                                        <Box className={classes.header}>
                                            <Typography id="customized-dialog-title" variant='h6-medium'>
                                                {
                                                    (clientMock && "Mock Interview Details") ||
                                                    ((userMock || dafInterview) && "Personalized Interview Details") ||
                                                    (codingInterview && "Coding challenge") ||
                                                    ((template?.type === 'case_master' || stateParams?.data === 'case_master') && "Case Study Interview") ||
                                                    "Assessment"
                                                }
                                            </Typography>
                                            <Box display='flex' gap={2} alignItems='center'>
                                                <IconButton className={classes.closeIcon} onClick={handleClose}>
                                                    <ClearIcon fontSize='small' />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        <Divider />

                                        <DialogContent className={classes.contentContainer}>
                                            <Box display='flex' gap={1} alignItems='center' mb={1}>
                                                <img
                                                    src='https://assets.languify.in/images/assessment-icon.svg'
                                                    alt='Assessment'
                                                />
                                                <Typography className={classes.label}>
                                                    {clientMock && 'Mock Interview:' ||
                                                        (userMock || dafInterview) && 'Personalized Interview' ||
                                                        codingInterview && 'Challenge:' ||
                                                        'Assessment:'}
                                                </Typography>
                                                <Typography className={classes.value} color='primary'>
                                                    {template?.name}
                                                </Typography>
                                            </Box>
                                            <Box display='flex' gap={1} alignItems='center' mb={1}>
                                                <img
                                                    src='https://assets.languify.in/images/time-icon.svg'
                                                    alt='Assessment'
                                                />
                                                <Typography className={classes.label}>
                                                    {
                                                        template?.type === 'pathway' ?
                                                            'Test Time :' : codingInterview ? 'Overall Maximum Time (upto):' : 'Maximum Time Allowed :'
                                                    }
                                                </Typography>
                                                <Typography className={classes.value} color='primary'>
                                                    {testTime ?
                                                        (testTime > 60) ?
                                                            `${Math.ceil((testTime) / 60)} minutes` :
                                                            `${template.metadata.testTime} secs`
                                                        : codingInterview ? '60 mins' : (template?.type === 'case_master' || stateParams?.data === 'case_master') ? '30 mins' : "5-10 mins"
                                                    }
                                                </Typography>
                                            </Box>
                                            {!codingInterview && !(template?.type === 'case_master' && stateParams?.data === 'contest') && (
                                                <Box display='flex' gap={1} alignItems='center'>
                                                    <img
                                                        src='https://assets.languify.in/images/deadline-icon.svg'
                                                        alt='Assessment'
                                                    />
                                                    <Typography className={classes.label}>Deadline : </Typography>
                                                    <Typography className={classes.value} color='#C51407'>
                                                        {
                                                            template.expiresOn &&
                                                                dayjs(template.expiresOn).year() !== neverExpiryYear ?
                                                                formatDate(template.expiresOn, "DD MMMM, hA") :
                                                                "Never expires"
                                                        }
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Typography variant='body01-bold' mb={1} mt={3}>
                                                {codingInterview ? 'Instructions:' : 'General Instructions'}
                                            </Typography>
                                            <ul>
                                                {(codingInterview
                                                    ? instructions['coding']
                                                    : (template?.type === 'case_master' || stateParams?.data === 'case_master')
                                                        ? (stateParams?.data === 'contest' ? instructions['case_master_contest'] : instructions['case_master'])
                                                        : instructions[template?.type]
                                                )?.map((instruction, i) => (
                                                    <li key={i} className={classes.instructions}>
                                                        {instruction}
                                                    </li>
                                                ))}
                                            </ul>

                                        </DialogContent>
                                        <Divider />

                                        <Typography align='center' variant='body02-bold' my={2} color='success.clr-700'>
                                            {codingInterview ?
                                                'Sit in a quiet environment & use headphones for the best experience' :
                                                'Remember to sit in a noise free environment'
                                            }
                                        </Typography>

                                        <Box display='flex' alignItems='center' justifyContent='center' gap={2} mb={2} mt={1}>
                                            <Typography variant='h6-regular'>
                                                Ready to begin?
                                            </Typography>
                                            <CustomButton
                                                variant='contained'
                                                disabled={state !== 'GRANTED'}
                                                onClick={() => setMatched(true)}
                                            >
                                                Start Assessment
                                            </CustomButton>
                                        </Box>

                                        <Box className={classes.micRow}>
                                            <MicIcon style={{ fontSize: '18px' }} />
                                            <Box sx={{ width: '120px' }}>
                                                <LinearProgress
                                                    variant="determinate" value={audioLevel}
                                                    style={{ borderRadius: '10px' }}
                                                />
                                            </Box>
                                        </Box>

                                        {
                                            (state === 'ASK_PERMISSION') &&
                                            <Box
                                                display='flex' alignItems='center' gap={2}
                                                width={'100%'} justifyContent='center'
                                            >
                                                <Typography fontSize={15} color='error' fontWeight={500}>
                                                    It seems you have blocked mic or camera permissions!
                                                </Typography>

                                                <CustomButton
                                                    variant='contained'
                                                    onClick={openPermissionPrompt}
                                                >
                                                    Give Permission
                                                </CustomButton>
                                            </Box>
                                        }

                                        <Typography variant='body02-semiBold' textAlign='center' mb={2}>
                                            Facing any issue with mic? &nbsp;
                                            <a
                                                href='#' style={{ color: theme.palette.primary.main, textDecoration: 'underline' }}
                                                onClick={() => setOpenTroubleshootDialog(true)}
                                            >
                                                Troubleshoot here
                                            </a>
                                        </Typography>
                                    </>

                                    : <CircularProgressCountDown onDialogClose={handleClose} proceedToTest={proceedToTest} />
                            }
                        </Dialog>
                        <TroubleshootDialog
                            open={openTroubleshootDialog}
                            onClose={() => setOpenTroubleshootDialog(false)}
                        />
                    </>
                )
            }
        </>
    );
}

export default StartAssessmentDialog;